import { useState } from 'react'

import { Dialog } from '@/components/ui/dialog'
import type { GetUsersListFilters } from '@/modules/chatting/types'

import { DialogProps } from '../types'
import { CollectionInfo } from './CollectionInfo/CollectionInfo'
import {
  CollectionContext,
  useComputeCollectionContext,
} from './CollectionProvider'
import { CollectionsList } from './CollectionsList/CollectionsList'
import type { CollectionsItem } from './CollectionsList/types'
import { useCollectionsListData } from './useData'

export interface CollectionsDialogConfig {
  listId?: CollectionsItem['id']
  filters?: GetUsersListFilters
  openInCustomOrderEditMode?: boolean
}

interface Props extends DialogProps {
  config?: CollectionsDialogConfig
}

export const CollectionsDialog = ({ isOpen, onClose, config = {} }: Props) => {
  const { listId, openInCustomOrderEditMode } = config
  const listsData = useCollectionsListData()
  const [selectedCollectionId, setSelectedCollectionId] = useState(listId)

  const selectedCollection = listsData.lists.find(
    (list) => list.id === selectedCollectionId,
  )

  const collectionContextValue = useComputeCollectionContext({
    collection: selectedCollection,
    config,
  })

  const handleChangeSelectedCollectionId = (id?: CollectionsItem['id']) => {
    setSelectedCollectionId(id)
    collectionContextValue.resetCollectionUsersRequest()
  }

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      showCloseIcon={false}
      panelClassName="lg:max-w-[1292px] sm:max-w-full max-w-full p-6"
      childrenClassName="max-w-full overflow-hidden"
    >
      <div className="space-y-6">
        <CollectionContext.Provider value={collectionContextValue}>
          <div className="flex gap-6">
            <CollectionsList
              {...listsData}
              onSelectListId={handleChangeSelectedCollectionId}
              openInCustomOrderEditMode={!!openInCustomOrderEditMode}
              className={selectedCollection ? 'max-lg:hidden' : ''}
            />
            <CollectionInfo
              list={selectedCollection}
              className={!selectedCollection ? 'max-lg:hidden' : ''}
              onCloseSelectedList={() =>
                handleChangeSelectedCollectionId(undefined)
              }
            />
          </div>
        </CollectionContext.Provider>
        <Dialog.ClosableFooter
          onClose={onClose}
          closeText="Close"
          className="grid-cols-1"
        />
      </div>
    </Dialog>
  )
}
